/**
 * The view rank write's transport: the endpoint, the request it sends, the response it decodes,
 * and the kinds of error it tells apart.
 *
 * Layout-neutral. The request names cards rather than cells, so a list of the same view sends
 * the same one.
 */

import {create, fromJson, toJson} from '@github-ui/dotcom-schema/protobuf'
import {
  type BoardRankRequest,
  BoardRankRequestSchema,
  BoardRankResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/board_rank'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {BoardMoveFailedError, ViewRankStaleError} from '../components/board/board-move-errors'
import type {SavedViewsBoardRankRequest, SavedViewsBoardRankResult} from '../components/board/types'
import {readJson, toBoardMoveError} from './board-update-api'

/** Names this write in the schema-parse metrics. */
const QUERY_NAME = 'viewRank'

const UNREADABLE_MESSAGE = 'Could not move this item: the server answered about a different item.'

export function toBoardRankRequest({itemId, after, rankFirst}: SavedViewsBoardRankRequest) {
  return create(BoardRankRequestSchema, {id: itemId, after: after ?? undefined, rankFirst: [...rankFirst]})
}

/**
 * Sends one rank write to a view's `rank` endpoint.
 *
 * A 409 and a 404 both mean the cards the request named changed: the server answers 409 for a
 * card that lost its rank or left the view, and 404 for a card the viewer can no longer read.
 */
export async function postViewRank(
  request: SavedViewsBoardRankRequest,
  {path, routeId}: {path: string; routeId: string},
): Promise<SavedViewsBoardRankResult> {
  const message = toBoardRankRequest(request)

  let response: Response
  try {
    response = await reactFetchJSON(path, {method: 'POST', body: toJson(BoardRankRequestSchema, message)})
  } catch {
    throw new BoardMoveFailedError()
  }

  if (response.status === 404 || response.status === 409) throw new ViewRankStaleError()
  if (!response.ok) throw await toBoardMoveError(response)

  return toRankResult(await readJson(response), message, routeId)
}

function toRankResult(raw: unknown, message: BoardRankRequest, routeId: string): SavedViewsBoardRankResult {
  try {
    const decoded = fromJson({schema: BoardRankResponseSchema, raw, routeId, queryName: QUERY_NAME})
    if (decoded.id !== message.id) throw new BoardMoveFailedError(UNREADABLE_MESSAGE)

    return {itemId: decoded.id}
  } catch (error) {
    throw error instanceof BoardMoveFailedError ? error : new BoardMoveFailedError()
  }
}
